import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Space, Input, message, Modal, Button, Form, Select, InputNumber, Statistic, Row, Col, Empty, Popconfirm } from 'antd';
import { PlusOutlined, PauseOutlined, PlayCircleOutlined, StopOutlined, ReloadOutlined, DollarOutlined } from '@ant-design/icons';
import { accountApi, sellProfitApi } from '../services/api';

const STATUS_MAP = {
  running: { color: 'green', text: '监控中' },
  paused: { color: 'orange', text: '已暂停' },
  stopped: { color: 'default', text: '已停止' },
  completed: { color: 'blue', text: '已触发' },
};

export default function SellProfitPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [profitRate, setProfitRate] = useState(0.05);
  const [createLoading, setCreateLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [form] = Form.useForm();

  const openDetail = async (task) => {
    setDetailTask(task);
    setDetailModal(true);
    setDetailLoading(true);
    setDetailData([]);
    try {
      const res = await sellProfitApi.getDetails(task.id);
      if (res?.success) setDetailData(res.data || []);
      else message.error(res?.message || '加载明细失败');
    } catch (e) {
      message.error('加载明细失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setDetailLoading(false);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await sellProfitApi.list();
      if (res?.success) setTasks(res.data || []);
    } catch (e) {
      message.error('加载任务失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, []);

  const loadGroups = async () => {
    try {
      const res = await accountApi.getGroupedPositions();
      if (res?.success) setGroups(res.data || []);
    } catch (e) {
      message.error('加载持仓失败: ' + (e.response?.data?.message || e.message));
    }
  };

  const openModal = async () => {
    setModalOpen(true);
    setSelectedStock(null);
    setProfitRate(0.05);
    form.resetFields();
    await loadGroups();
  };

  // 当前选中股票的持仓客户
  const selectedHoldings = useMemo(() => {
    if (!selectedStock) return [];
    return selectedStock.clients || [];
  }, [selectedStock]);

  // 计算盈利总金额（当前持仓客户盈利之和）
  const totalProfit = useMemo(() => {
    if (!selectedStock) return 0;
    const clients = selectedStock.clients || [];
    return clients.reduce((sum, c) => sum + (c.profit_loss || 0), 0);
  }, [selectedStock]);

  // 参考成本价 = 持仓客户中最低成本价
  const minCost = useMemo(() => {
    if (!selectedStock) return 0;
    const clients = selectedStock.clients || [];
    let min = 0;
    clients.forEach(c => { if (c.cost_price > 0 && (min === 0 || c.cost_price < min)) min = c.cost_price; });
    return min;
  }, [selectedStock]);

  const targetPrice = minCost > 0 ? minCost * (1 + profitRate) : 0;

  const handleCreate = async () => {
    if (!selectedStock) { message.warning('请选择股票'); return; }
    setCreateLoading(true);
    try {
      const res = await sellProfitApi.create({
        name: selectedStock.stock_name,
        stock_code: selectedStock.stock_code,
        stock_name: selectedStock.stock_name,
        profit_rate: profitRate,
      });
      if (res?.success) {
        message.success('卖出止盈任务已创建');
        setModalOpen(false);
        loadTasks();
      } else {
        message.error(res?.message || '创建失败');
      }
    } catch (e) {
      message.error('创建失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setCreateLoading(false);
    }
  };

  const handlePause = async (id) => {
    try {
      const res = await sellProfitApi.pause(id);
      if (res?.success) { message.success('已暂停'); loadTasks(); }
      else message.error(res?.message || '暂停失败');
    } catch (e) { message.error('暂停失败: ' + e.message); }
  };

  const handleStart = async (id) => {
    try {
      const res = await sellProfitApi.start(id);
      if (res?.success) { message.success('已开始'); loadTasks(); }
      else message.error(res?.message || '开始失败');
    } catch (e) { message.error('开始失败: ' + e.message); }
  };

  const handleStop = async (id) => {
    try {
      const res = await sellProfitApi.stop(id);
      if (res?.success) { message.success('任务已停止'); loadTasks(); }
      else message.error(res?.message || '停止失败');
    } catch (e) { message.error('停止失败: ' + e.message); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await sellProfitApi.remove(id);
      if (res?.success) { message.success('任务已删除'); loadTasks(); }
      else message.error(res?.message || '删除失败');
    } catch (e) { message.error('删除失败: ' + e.message); }
  };

  const columns = [
    { title: '任务名称', dataIndex: 'stock_name', render: (v, r) => <b>{v}<br /><small style={{ color: '#888' }}>{r.stock_code}</small></b> },
    { title: '止盈比例', dataIndex: 'profit_rate', width: 90, render: v => `${(v * 100).toFixed(0)}%` },
    { title: '成本价', dataIndex: 'cost_price', width: 90, render: v => v ? `¥${Number(v).toFixed(2)}` : '-' },
    { title: '当前市价', dataIndex: 'current_price', width: 100, render: v => v ? <b style={{ color: '#1890ff' }}>¥{Number(v).toFixed(2)}</b> : '-' },
    { title: '止盈卖出价', dataIndex: 'target_price', width: 100, render: (v, r) => <b style={{ color: '#cf1322' }}>¥{Number(v || 0).toFixed(2)}</b> },
    { title: '持仓客户', dataIndex: 'client_count', width: 80, render: v => `${v || 0} 户` },
    { title: '盈利总金额', dataIndex: 'total_profit', width: 110, render: v => <span style={{ color: v > 0 ? '#cf1322' : '#888' }}>¥{Number(v || 0).toFixed(2)}</span> },
    { title: '已触发', dataIndex: 'triggered_count', width: 80, render: v => v || 0 },
    { title: '状态', dataIndex: 'status', width: 90, render: v => {
      const s = STATUS_MAP[v] || { color: 'default', text: v };
      return <Tag color={s.color}>{s.text}</Tag>;
    }},
    { title: '创建时间', dataIndex: 'created_at', width: 160, render: v => v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-' },
    { title: '操作', width: 320, fixed: 'right', render: (_, r) => (
      <Space size={2} wrap={false}>
        <Button size="small" onClick={() => openDetail(r)}>明细</Button>
        {r.status === 'running' ? (
          <Button size="small" icon={<PauseOutlined />} onClick={() => handlePause(r.id)}>暂停</Button>
        ) : r.status === 'paused' ? (
          <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={() => handleStart(r.id)}>开始</Button>
        ) : null}
        <Popconfirm title="确定停止并删除该任务？" onConfirm={() => handleStop(r.id)}>
          <Button size="small" danger icon={<StopOutlined />} disabled={r.status === 'completed'}>停止</Button>
        </Popconfirm>
        <Popconfirm title="确认删除该任务？" description="删除后不可恢复" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消">
          <Button size="small" danger type="link">删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <Card
      title={<><DollarOutlined /> 卖出止盈任务</>}
      size="small"
      extra={<Space>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadTasks}>刷新</Button>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openModal}>新增任务</Button>
      </Space>}
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}><Statistic title="任务总数" value={tasks.length} /></Col>
        <Col span={8}><Statistic title="监控中" value={tasks.filter(t => t.status === 'running').length} valueStyle={{ color: '#3f8600' }} /></Col>
        <Col span={8}><Statistic title="已触发" value={tasks.filter(t => t.status === 'completed').length} valueStyle={{ color: '#cf1322' }} /></Col>
      </Row>

      <Table rowKey="id" size="small" loading={loading} dataSource={tasks} columns={columns} pagination={{ pageSize: 10 }} scroll={{ x: 1500 }} />

      <Modal title="新增卖出止盈任务" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate} confirmLoading={createLoading} width={720}>
        <Form form={form} layout="vertical">
          <Form.Item label="选择持仓股票">
            <Select
              showSearch
              placeholder="搜索并选择股票"
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={selectedStock?.stock_code}
              onChange={(code) => {
                const g = groups.find(x => x.stock_code === code);
                setSelectedStock(g || null);
                setProfitRate(0.05);
              }}
              options={groups.map(g => ({ value: g.stock_code, label: `${g.stock_name}(${g.stock_code})` }))}
            />
          </Form.Item>

          {selectedStock && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="止盈比例（1%-20%）" required>
                    <InputNumber
                      min={1} max={20} value={profitRate * 100}
                      formatter={v => `${v}%`} parser={v => v.replace('%', '')}
                      onChange={v => setProfitRate((Number(v) || 0) / 100)}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="参考成本价（最低）">
                    <b>¥{minCost.toFixed(2)}</b>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="止盈卖出价">
                    <b style={{ color: '#cf1322' }}>¥{targetPrice.toFixed(2)}</b>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={`持仓客户列表（${selectedHoldings.length} 户，盈利总金额 ¥${totalProfit.toFixed(2)}）`}>
                <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                  {selectedHoldings.length === 0 ? <Empty description="无持仓客户" style={{ margin: 16 }} /> : (
                    <Table
                      rowKey="client_id"
                      size="small"
                      pagination={false}
                      dataSource={selectedHoldings}
                      columns={[
                        { title: '客户', dataIndex: 'client_name', render: (v, r) => `${v || r.client_id}` },
                        { title: '持仓', dataIndex: 'quantity', width: 80, render: v => v || 0 },
                        { title: '成本价', dataIndex: 'cost_price', width: 90, render: v => `¥${Number(v || 0).toFixed(2)}` },
                        { title: '现价', dataIndex: 'current_price', width: 90, render: v => `¥${Number(v || 0).toFixed(2)}` },
                        { title: '持仓金额', dataIndex: 'amount', width: 100, render: v => `¥${Number(v || 0).toFixed(2)}` },
                        { title: '盈亏', dataIndex: 'profit_loss', width: 100, render: v => <span style={{ color: v > 0 ? '#cf1322' : '#3f8600' }}>¥{Number(v || 0).toFixed(2)}</span> },
                      ]}
                    />
                  )}
                </div>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={detailTask ? `每人止盈明细 - ${detailTask.stock_name}(${detailTask.stock_code})` : '每人止盈明细'}
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        footer={null}
        width={1200}
      >
        <Table
          rowKey="id"
          size="small"
          loading={detailLoading}
          dataSource={detailData}
          pagination={{ pageSize: 10, showTotal: t => `共 ${t} 户`, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          scroll={{ x: 850 }}
          summary={(data) => {
            // 合计基于全量明细（detailData），而非当前页
            const totalQty = detailData.reduce((s, d) => s + (d.quantity || 0), 0);
            const totalProfit = detailData.reduce((s, d) => s + (d.profit_amount || 0), 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><b>合计</b></Table.Summary.Cell>
                <Table.Summary.Cell index={1}><b>{detailData.length} 户</b></Table.Summary.Cell>
                <Table.Summary.Cell index={2}><b>{totalQty}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
                <Table.Summary.Cell index={5}><b>止盈金额: ¥{Number(totalProfit).toFixed(2)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={6}></Table.Summary.Cell>
                <Table.Summary.Cell index={7}></Table.Summary.Cell>
                <Table.Summary.Cell index={8}></Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
          columns={[
            { title: '客户', dataIndex: 'client_name', render: (v, r) => `${v || r.client_id}` },
            { title: '持仓量', dataIndex: 'quantity', width: 80, render: v => v || 0 },
            { title: '成本价', dataIndex: 'cost_price', width: 90, render: v => `¥${Number(v || 0).toFixed(2)}` },
            { title: '当前价', dataIndex: 'current_price', width: 90, render: v => <b style={{ color: '#1890ff' }}>¥{Number(v || 0).toFixed(2)}</b> },
            { title: '止盈卖出价', dataIndex: 'target_price', width: 100, render: v => <b style={{ color: '#cf1322' }}>¥{Number(v || 0).toFixed(2)}</b> },
            { title: '止盈金额', dataIndex: 'profit_amount', width: 110, render: v => <span style={{ color: v > 0 ? '#cf1322' : '#888' }}>¥{Number(v || 0).toFixed(2)}</span> },
            { title: '持仓金额', dataIndex: 'amount', width: 110, render: v => `¥${Number(v || 0).toFixed(2)}` },
            { title: '状态', dataIndex: 'status', width: 90, render: v => {
              const map = { pending: ['default', '待触发'], triggered: ['green', '已卖出'], failed: ['red', '失败'] };
              const [color, text] = map[v] || ['default', v];
              return <Tag color={color}>{text}</Tag>;
            }},
            { title: '触发时间', dataIndex: 'triggered_at', width: 160, render: v => v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-' },
          ]}
        />
      </Modal>
    </Card>
  );
}
